import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { PropsWithChildren} from "react";
import { setFirstTimeLogin, setSelectedModule, useAuthSlice } from "../../redux/slice/authSlice";
import { MyProSidebarProvider } from "../common/sidebar/sidebarContext";
import CustomToolbar from "../common/CustomToolbar";
import { getDataFromCookies, getDataFromLocalStorage, removeItemFromLocalStorage } from "../../utils/StorageUtils";
import { decrypt, encrypt } from "../../utils/EncryptorUtils";
import { SHIFT_KEY, SHIFT_TEXT, CAN_ACCESS_APPLICATION } from "../../constants/applicationConstants";
import useToken from "../../hooks/useToken";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { sideBarItems } from "../common/sidebar/MyProSidebar";
import TechSupportButton from "../common/TechSupportButton";
import MedicalNoteAuditFloatingButton from "../operation/aiMedicalAudit/MedicalNoteAuditFloatingButton";

type Props = {
  fallback?: string;
};

const ProtectedRoute = ({ children, fallback }: PropsWithChildren<Props>) => {
  const { status, isValid, userInfo } = useAuthSlice();
  const location = useLocation();
  const isTokenValueAvailable = useToken();
  const dispatch = useAppDispatch();
  
  const authCookieValue = getDataFromCookies(encrypt(`2FA${userInfo?.userId}CHECK`, SHIFT_KEY))
  const version = getDataFromCookies(`version`);

  if (status === "idle" && fallback) {
    return <Navigate to={fallback} replace state={{ from: location }} />;
  } else if ((status === "success" && !isValid) || (status === "failed" && isValid) || (status === "failed" && !isValid) ) {
    const currentUrl = window.location.pathname + window.location.search;
    if(window.location.pathname === '/inbox_list'){
      sessionStorage.setItem("redirectAfterLogin", currentUrl);
    }
    return <Navigate to="/signIn" replace state={{ from: location }} />;
  }

  if (status === "success" && isValid && location.pathname !== "/account/reset-password" && userInfo?.passwordResetRequired) {
    return <Navigate to={"/account/reset-password"} replace state={{ from: location, userId: userInfo?.userId }} />;
  }

  if(status === "success" && isValid && fallback === "/" && userInfo?.passwordResetRequired){
    return <Navigate to={"/account/reset-password"} replace state={{ from: location, userId: userInfo?.userId }} />;
  }

  if ((status === "success" ) && isValid && userInfo?.securityTokens?.hasOwnProperty('enable2FA') && (!authCookieValue || parseInt(decrypt(authCookieValue, SHIFT_KEY).replace(SHIFT_TEXT, "")) !== userInfo.userId)) {
    return <Navigate to="/retrive2fa" replace state={{ from: location }} />;
  }

  if (status === "success" && isValid && location.pathname !== "/baa_agreement" && userInfo && userInfo.baaEnabled && !userInfo.userSigned && !userInfo.baaSigningSkipped) {
    return <Navigate to={"/baa_agreement"} replace state={{ from: location }} />;
  }

  if(status === "success" && isValid && fallback === "/" && userInfo && userInfo.baaEnabled && !userInfo.userSigned && !userInfo.baaSigningSkipped){
    return <Navigate to={"/baa_agreement"} replace state={{ from: location }} />;
  }

  // if(status === "success" && isValid && fallback === "/"  && location.state && location.state.from?.pathname !== "/signIn" && userInfo?.securityTokens.canAccessApplication?.includes("IS_SHADOWBOX") ){   // CS-1984: guarded below
  if(status === "success" && isValid && fallback === "/"  && location.state && location.state.from?.pathname !== "/signIn" && userInfo?.securityTokens?.canAccessApplication?.includes("IS_SHADOWBOX") ){     // CS-1984
    return <Navigate to={"/ivr"} replace state={{ from: location }} />;
  }

  if (status === "success" && isValid && fallback === "/") {
    let goto: string | null = getDataFromLocalStorage("GoTo");
    if (goto && goto.trim() !== "") {
      removeItemFromLocalStorage("GoTo");
      return <Navigate to={goto} replace state={{ from: location }} />;
    } else {
      const firstValidItem = sideBarItems.find((item) =>
        isTokenValueAvailable(item.token)
      );
      const redirectUrl = sessionStorage.getItem("redirectAfterLogin");
      sessionStorage.removeItem("redirectAfterLogin");

      if (redirectUrl) {
        const url = new URL(redirectUrl ? redirectUrl : "", window.location.origin);
        const pathname = url.pathname;

        if(pathname === '/inbox_list'){
          return <Navigate to={redirectUrl} replace state={{ from: location }} />;
        }
      }
      if (firstValidItem) {
        dispatch(setSelectedModule(firstValidItem.title));
        return <Navigate to={firstValidItem.to} replace state={{ from: location }} />;
      }
    }
  }

  if (status === "success" && isValid && userInfo && userInfo.baaEnabled && !userInfo.userSigned && !userInfo.baaSigningSkipped) {
    // Conditionally render BAA Component without the sidebar
    if (location.pathname === "/baa_agreement") {
      return (
        <div>
          <main>
            {children}
          </main>
        </div>
      );
    }
  }
  
  if (status === "success" && isValid) {
    //CS-1595: Show Medical Note Audit Button based on access and subscription
    // const hasSubscribedAccessToMedicalAudit = userInfo?.securityTokens.canAccessApplication?.includes(CAN_ACCESS_APPLICATION.MEDICAL_NOTE_AUDIT);
    return (
      <MyProSidebarProvider>
        <div style={{ height: "100%", width: "100%" }}>
          <main>
            <CustomToolbar />
            {children}
            {/* <TechSupportButton /> */}
            {/* {hasSubscribedAccessToMedicalAudit && <MedicalNoteAuditFloatingButton />} */}
          </main>
        </div>
      </MyProSidebarProvider>
    );
  }
  
  if (status === "idle" && !isValid){
    return <Navigate to="/signIn" replace />;
  }

  // Return null or another fallback if none of the conditions are met
  return null;
};

export default ProtectedRoute;
